import { createPortal } from "react-dom";

import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";

import type {
  LeaderKeyCommand,
  LeaderKeyPickerState,
} from "@/hooks/use-leader-keys";

interface LeaderKeysIndicatorProps {
  isPending: boolean;
  sequences: readonly LeaderKeyCommand[];
  picker?: LeaderKeyPickerState | null;
}

function getLeaderShortcutLabel(): string {
  if (typeof navigator === "undefined") return "Ctrl+G";
  return /Mac|iPhone|iPad|iPod/.test(navigator.platform) ? "⌘G" : "Ctrl+G";
}

function getFollowUpKeyLabel(code: string): string {
  return code.startsWith("Key") ? code.slice(3).toLowerCase() : code;
}

/**
 * Quiet, non-interactive feedback while a serial leader-key sequence is armed.
 *
 * Rendered through a portal into ``document.body`` so it escapes the flex
 * ``#root`` shell: ``position: fixed`` inside a flex container is sized
 * inconsistently across engines (Safari can stretch the fixed item to the
 * container's cross size instead of its content), which previously made the
 * indicator fill the viewport. Body is a plain block, so fixed positioning
 * pins to the viewport everywhere.
 */
export function LeaderKeysIndicator({
  isPending,
  sequences,
  picker = null,
}: LeaderKeysIndicatorProps) {
  const { t } = useTranslation();

  if (
    !isPending
    || (!picker && sequences.length === 0)
    || typeof document === "undefined"
  ) {
    return null;
  }

  return createPortal(
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex justify-center px-4"
    >
      {picker ? (
        <Card className="w-full max-w-sm border-border/60 bg-muted/95 px-3 py-2 text-xs text-muted-foreground">
          <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
            <span className="font-medium text-foreground">
              {t("leaderKeys.armed", { leader: getLeaderShortcutLabel() })}
            </span>
            <span aria-hidden="true">·</span>
            <span className="font-medium text-foreground">{picker.label}</span>
          </div>
          {picker.status === "ready" ? (
            <ol aria-label={picker.label} className="mt-2 grid gap-1">
              {picker.items.map((item, index) => (
                <li key={item.id} className="flex min-w-0 items-center gap-2">
                  <kbd className="font-mono text-foreground">{index + 1}</kbd>
                  <span className="truncate text-foreground">{item.label}</span>
                </li>
              ))}
            </ol>
          ) : (
            <p className="mt-2 text-center">{picker.message}</p>
          )}
          <p className="mt-2 text-center">{t("leaderKeys.cancelHint")}</p>
        </Card>
      ) : (
        <Card className="w-full max-w-sm border-border/60 bg-muted/95 px-3 py-2 text-xs text-muted-foreground">
          <p className="text-center font-medium text-foreground">
            {t("leaderKeys.armed", { leader: getLeaderShortcutLabel() })}
          </p>
          <ol className="mt-2 grid gap-1">
            {sequences.map((sequence) => (
              <li key={sequence.code} className="flex min-w-0 items-center gap-2">
                <kbd className="font-mono text-foreground">
                  {getFollowUpKeyLabel(sequence.code)}
                </kbd>
                <span className="truncate text-foreground">{sequence.label}</span>
              </li>
            ))}
          </ol>
          <p className="mt-2 text-center">{t("leaderKeys.cancelHint")}</p>
        </Card>
      )}
    </div>,
    document.body,
  );
}
